<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { trackerState, type OpenEvent } from "../tracker/store.svelte";
  import { startPolling, stopPolling, syncOnce } from "../tracker/sync";
  import Icon from "./shared/Icon.svelte";

  // === Lifecycle ===========================================================
  onMount(() => {
    startPolling();
  });
  onDestroy(() => {
    stopPolling();
  });

  // === Group by pixel_id puis bucket par jour ===============================

  interface Grouped {
    last: OpenEvent;
    count: number;
  }

  /** Bucket par fenêtre temporelle. Calculé sur "le plus récent open" du pixel. */
  type Bucket = "today" | "yesterday" | "thisWeek" | "older";

  function bucketOf(unixSec: number): Bucket {
    const now = new Date();
    const open = new Date(unixSec * 1000);

    // Reset à minuit local pour comparer "même jour"
    const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const todayStart = dayStart(now);
    const yesterdayStart = todayStart - 86400_000;
    const weekStart = todayStart - 6 * 86400_000;

    const t = open.getTime();
    if (t >= todayStart) return "today";
    if (t >= yesterdayStart) return "yesterday";
    if (t >= weekStart) return "thisWeek";
    return "older";
  }

  const BUCKET_LABELS: Record<Bucket, string> = {
    today: "Aujourd'hui",
    yesterday: "Hier",
    thisWeek: "Cette semaine",
    older: "Plus ancien",
  };
  const BUCKET_ORDER: Bucket[] = ["today", "yesterday", "thisWeek", "older"];

  const groupedByBucket = $derived.by<Record<Bucket, Grouped[]>>(() => {
    // 1. Group by pixel_id
    const byPixel = new Map<string, Grouped>();
    for (const o of trackerState.opens) {
      const existing = byPixel.get(o.pixel_id);
      if (existing) {
        existing.count++;
        if (o.opened_at > existing.last.opened_at) existing.last = o;
      } else {
        byPixel.set(o.pixel_id, { last: o, count: 1 });
      }
    }

    // 2. Bucket par fenêtre temporelle, garde l'ordre DESC dans chaque bucket
    const out: Record<Bucket, Grouped[]> = {
      today: [], yesterday: [], thisWeek: [], older: [],
    };
    for (const g of byPixel.values()) {
      out[bucketOf(g.last.opened_at)].push(g);
    }
    for (const bucket of BUCKET_ORDER) {
      out[bucket].sort((a, b) => b.last.opened_at - a.last.opened_at);
    }
    return out;
  });

  const hasAny = $derived(
    BUCKET_ORDER.some((b) => groupedByBucket[b].length > 0),
  );

  // === Helpers UI ==========================================================
  function relativeTime(unixSec: number): string {
    const diff = Math.floor(Date.now() / 1000) - unixSec;
    if (diff < 5) return "à l'instant";
    if (diff < 60) return `${diff} sec`;
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} j`;
    return new Date(unixSec * 1000).toLocaleDateString("fr-FR");
  }

  function flagEmoji(country: string | null): string {
    if (!country || country.length !== 2) return "";
    const A = 0x1f1e6 - 0x41;
    return String.fromCodePoint(country.charCodeAt(0) + A, country.charCodeAt(1) + A);
  }

  function initials(input: string): string {
    const trimmed = input.trim();
    if (!trimmed) return "?";
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // === Compteurs d'en-tête =================================================
  // « Envois jamais ouverts » du handoff n'est pas calculable ici : le Worker
  // n'expose que les OUVERTURES (`/me/opens`), jamais la liste des pixels
  // enregistrés à l'envoi. Le troisième compteur dit donc ce qu'on sait —
  // combien d'envois distincts ont été suivis.
  const stats = $derived.by(() => {
    const opens = trackerState.opens;
    const today = opens.filter((o) => bucketOf(o.opened_at) === "today").length;
    const people = new Set(opens.map((o) => o.recipient_email.toLowerCase())).size;
    const sends = new Set(opens.map((o) => o.pixel_id)).size;
    return { today, people, sends };
  });

  /** Délai entre l'envoi et la première ouverture — le vrai signal. */
  function delay(sentAt: number, openedAt: number): string {
    const d = Math.max(0, openedAt - sentAt);
    if (d < 60) return `${d}s`;
    if (d < 3600) return `${Math.floor(d / 60)} min`;
    if (d < 86400) return `${Math.floor(d / 3600)} h`;
    return `${Math.floor(d / 86400)} j`;
  }

  /** Appareil et navigateur, lus grossièrement dans l'UA. */
  function device(ua: string | null): string {
    if (!ua) return "";
    const os = /iPhone/i.test(ua) ? "iPhone"
      : /iPad/i.test(ua) ? "iPad"
      : /Android/i.test(ua) ? "Android"
      : /Macintosh/i.test(ua) ? "Mac"
      : /Windows/i.test(ua) ? "Windows"
      : "";
    const br = /Edg\//i.test(ua) ? "Edge"
      : /Chrome\//i.test(ua) ? "Chrome"
      : /Firefox\//i.test(ua) ? "Firefox"
      : /Safari\//i.test(ua) ? "Safari"
      : "";
    return [os, br].filter(Boolean).join(" · ");
  }

  function openDetail(pixelId: string) {
    trackerState.detailPixelId = pixelId;
  }

  // Tick toutes les 30s pour rafraîchir les "il y a X min" sans hit le réseau
  let tick = $state(0);
  let tickTimer: ReturnType<typeof setInterval>;
  onMount(() => {
    tickTimer = setInterval(() => (tick += 1), 30 * 1000);
  });
  onDestroy(() => clearInterval(tickTimer));
  $effect(() => {
    tick;
  });
</script>

<div class="dashboard">
  <header class="head">
    <div class="title-row">
      <div>
        <h1>Suivi</h1>
        <div class="meta">
          {stats.sends} envoi{stats.sends > 1 ? "s" : ""} suivi{stats.sends > 1 ? "s" : ""}
          · {trackerState.opens.length} ouverture{trackerState.opens.length > 1 ? "s" : ""}
        </div>
      </div>
      <button
        class="icon-btn"
        onclick={() => syncOnce()}
        disabled={trackerState.loading}
        aria-label="Rafraîchir"
      >
        {#if trackerState.loading}
          <span class="spinner" aria-hidden="true"></span>
        {:else}
          <Icon name="refresh" size={16} />
        {/if}
      </button>
    </div>

    <div class="stats">
      <div class="stat">
        <div class="n good">{stats.today}</div>
        <div class="lab">Ouvertures aujourd'hui</div>
      </div>
      <div class="stat">
        <div class="n">{stats.people}</div>
        <div class="lab">Destinataires distincts</div>
      </div>
      <div class="stat">
        <div class="n muted-n">{stats.sends}</div>
        <div class="lab">Envois suivis</div>
      </div>
    </div>
  </header>

  {#if trackerState.error}
    <p class="err">{trackerState.error}</p>
  {/if}

  {#if !hasAny && !trackerState.loading}
    <div class="empty">
      <p>Pas encore d'ouverture.</p>
      <p class="sub">
        Envoie un mail suivi depuis Mailfox. Quand le destinataire l'ouvre, ça
        apparaît ici en quelques secondes.
      </p>
    </div>
  {:else}
    {#each BUCKET_ORDER as bucket (bucket)}
      {#if groupedByBucket[bucket].length > 0}
        <div class="group-head">
          <span>{BUCKET_LABELS[bucket]}</span>
          <span class="n">{groupedByBucket[bucket].length}</span>
        </div>
        <ul class="opens">
          {#each groupedByBucket[bucket] as item (item.last.pixel_id)}
            {@const o = item.last}
            <li>
              <button class="open" onclick={() => openDetail(o.pixel_id)}>
                <span class="avatar" aria-hidden="true">
                  {initials(o.recipient_name ?? o.recipient_email)}
                </span>
                <span class="mid">
                  <span class="who">{o.recipient_name ?? o.recipient_email}</span>
                  <span class="subject">{o.subject ?? "(sans objet)"}</span>
                  <span class="place">
                    {#if o.country}{flagEmoji(o.country)} {o.city ?? o.country}{/if}
                    {#if o.country && device(o.user_agent)} · {/if}
                    {device(o.user_agent)}
                  </span>
                </span>
                <span class="right">
                  <span class="delay">{delay(o.sent_at, o.opened_at)}</span>
                  <span class="count">
                    <Icon name="eye" size={10} />{item.count}
                  </span>
                </span>
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    {/each}
  {/if}
</div>

<style>
  .dashboard { background: var(--bg); }

  /* === En-tête ============================================================ */
  .head {
    padding: calc(env(safe-area-inset-top) + 14px) 16px 14px;
    border-bottom: 1px solid var(--border-subtle);
  }
  .title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }
  h1 {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 31px;
    line-height: 1;
    letter-spacing: -0.015em;
  }
  .meta {
    margin-top: 7px;
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .icon-btn {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: var(--radius-md);
    color: var(--fg-muted);
  }
  .icon-btn:disabled { opacity: 0.5; }

  .stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 7px;
    margin-top: 14px;
  }
  .stat {
    padding: 10px 11px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    background: var(--bg-elev);
  }
  .stat .n {
    font-family: var(--font-mono);
    font-size: 21px;
    font-weight: 500;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: var(--fg);
  }
  .stat .n.good { color: var(--good); }
  .stat .n.muted-n { color: var(--fg-muted); }
  .stat .lab {
    margin-top: 5px;
    font-size: 10.5px;
    line-height: 1.3;
    color: var(--fg-subtle);
  }

  /* === Groupes ============================================================ */
  .group-head {
    position: sticky;
    top: 0;
    z-index: 4;
    display: flex;
    justify-content: space-between;
    padding: 12px 16px 5px;
    background: color-mix(in srgb, var(--bg) 92%, transparent);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    font-family: var(--font-mono);
    font-size: 9.5px;
    font-weight: 500;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .group-head .n { opacity: 0.75; font-variant-numeric: tabular-nums; }

  /* === Ligne d'ouverture ================================================== */
  .opens { list-style: none; margin: 0; padding: 0; }
  .open {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    column-gap: 11px;
    align-items: center;
    width: 100%;
    padding: 10px 16px;
    border-bottom: 1px solid var(--border-subtle);
    text-align: left;
  }
  .open:active { background: var(--bg-hover); }

  /* Le vert dit « ouvert » sans avoir besoin d'un libellé. */
  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--good-bg);
    color: var(--good);
    font-size: 11.5px;
    font-weight: 600;
  }

  .mid {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .who, .subject, .place {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .who { font-size: 13px; font-weight: 550; color: var(--fg); }
  .subject { font-size: 12px; color: var(--fg-muted); }
  .place {
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.04em;
    color: var(--fg-subtle);
  }

  .right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
  }
  .delay {
    font-family: var(--font-mono);
    font-size: 10.5px;
    font-variant-numeric: tabular-nums;
    color: var(--good);
  }
  .count {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-family: var(--font-mono);
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    color: var(--fg-subtle);
  }

  /* === Divers ============================================================= */
  .err {
    margin: 10px 12px;
    padding: 10px 12px;
    border-radius: var(--radius-lg);
    background: var(--bad-bg);
    color: var(--bad);
    font-size: 12.5px;
  }
  .empty { padding: 4rem 2rem; text-align: center; }
  .empty p {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-size: 22px;
    color: var(--fg-subtle);
  }
  .empty .sub {
    margin-top: 10px;
    font-family: var(--font-sans);
    font-style: normal;
    font-size: 12.5px;
    line-height: 1.5;
  }

  .spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--border);
    border-top-color: var(--fg);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
